import { useQuery } from "@tanstack/react-query";
import { useUser } from "@clerk/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { ForecastDay, Location } from "@/types/weather";
import { detailedForecastPath } from "@/lib/forecast-presentation";
import { calendarDay, localDate } from "@shared/forecast";

interface DetailedForecastHour {
  validAt: string;
  time: string;
  hour: number;
  waveHeight: string;
  wavePeriod: string;
  waveDirection: string;
  windSpeed: string;
  windDirection: string;
}

interface DetailedForecastResponse {
  location: string;
  date: string;
  dayOffset: number;
  hourlyData: DetailedForecastHour[];
  kind: string;
  source: string;
  windSource: string;
  fetchedAt: string;
  modelIssuedAt: string | null;
  timezone: string;
}

interface DetailedForecastModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  location: Location;
  day: ForecastDay;
}

export default function DetailedForecastModal({
  open,
  onOpenChange,
  location,
  day,
}: DetailedForecastModalProps) {
  const { user, isLoaded: authLoaded } = useUser();
  const owner = user?.id ?? "anonymous";
  const { data, isLoading, isError, refetch } = useQuery<DetailedForecastResponse>({
    queryKey: ["detailed-forecast", location.id, day.dayOffset, owner, day.dateKey],
    queryFn: async () => {
      if (!day.timezone || calendarDay(localDate(Date.now(), day.timezone), day.dayOffset) !== day.dateKey)
        throw new Error("Selected forecast date has changed");
      const response = await fetch(detailedForecastPath(location.id, day.dayOffset), { credentials: "include" });
      if (!response.ok) throw new Error("Hourly forecast unavailable");
      const result: DetailedForecastResponse = await response.json();
      if (result.date !== day.dateKey) throw new Error("Selected forecast date has changed");
      return result;
    },
    enabled: open && authLoaded,
    staleTime: 15 * 60 * 1000,
    retry: 1,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-xl max-h-[88dvh] overflow-y-auto"
        style={{
          background: "#071528",
          border: "1px solid rgba(255,255,255,0.1)",
          color: "#e2e8f0",
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-white text-base font-bold">
            {day.date} · Hourly model forecast
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-12 w-full rounded-lg bg-white/[0.06]" />)}
          </div>
        ) : isError ? (
          <div className="rounded-xl p-4 text-center" style={{ background: "rgba(255,255,255,0.03)" }}>
            <p className="text-[12px] text-slate-400">Hourly model data is unavailable for this day.</p>
            <button onClick={() => refetch()} className="mt-2 text-[11px] font-semibold text-emerald-300 underline underline-offset-2">
              Try again
            </button>
          </div>
        ) : data?.hourlyData?.length ? (
          <div className="w-full overflow-hidden rounded-xl border border-white/[0.06]">
            <table className="w-full table-fixed border-collapse text-left">
              <colgroup>
                <col className="w-[30%]" />
                <col className="w-[35%]" />
                <col className="w-[35%]" />
              </colgroup>
              <thead className="bg-white/[0.025]">
                <tr className="text-[9px] uppercase tracking-wider font-semibold text-slate-500">
                  <th scope="col" className="px-2 py-2 font-semibold">Local time</th>
                  <th scope="col" className="px-2 py-2 font-semibold">Waves</th>
                  <th scope="col" className="px-2 py-2 font-semibold">Wind</th>
                </tr>
              </thead>
              <tbody>
                {data.hourlyData.map((hour, index) => (
                  <tr key={hour.validAt || `${hour.hour}-${index}`} className="border-t border-white/[0.05] text-[11px]">
                    <th scope="row" className="break-words px-2 py-2 text-left font-medium leading-snug text-slate-400">
                      {hour.time}
                    </th>
                    <td className="break-words px-2 py-2 leading-snug">
                      <span className="block font-semibold text-emerald-300">{hour.waveHeight}</span>
                      <span className="mt-0.5 block text-[10px] text-slate-400">
                        {hour.wavePeriod} <span aria-hidden="true">·</span> <span>{hour.waveDirection?.trim() || "Direction unavailable"}</span>
                      </span>
                    </td>
                    <td className="break-words px-2 py-2 leading-snug">
                      <span className="block font-semibold text-sky-300">{hour.windSpeed}</span>
                      <span className="mt-0.5 block text-[10px] text-slate-400">{hour.windDirection}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="rounded-xl px-3 py-4 text-[11px] text-slate-500 text-center"
            style={{ background: "rgba(255,255,255,0.03)" }}>
            No trusted hourly model values are available for this day.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
